import { keys } from "@/constants/settings";

// Inicializa o acordeão de FAQ
export function initializeAccordion() {
	const accordionItems = document.querySelectorAll(".accordion-item");

	accordionItems.forEach((item) => {
		const header = item.querySelector(".accordion-header") as HTMLElement;
		const content = item.querySelector(".accordion-content") as HTMLElement;
		const chevron = header.querySelector(".accordion-chevron");

		if (header && content && chevron) {
			header.addEventListener("click", () => {
				const expanded = header.getAttribute("aria-expanded") === "true";
				header.setAttribute("aria-expanded", (!expanded).toString());
				chevron.classList.toggle("rotate-180");

				if (expanded) {
					content.style.opacity = "0";
					setTimeout(() => {
						content.style.maxHeight = "0px";
					}, 150);
				} else {
					content.style.maxHeight = `${content.scrollHeight}px`;
					setTimeout(() => {
						content.style.opacity = "1";
					}, 50);
				}
			});
		}
	});
}

// Inicializa o menu mobile
export function initializeMobileMenu() {
	const mobileMenuButton = document.getElementById("mobile-menu-button");
	const mobileMenu = document.getElementById("mobile-menu");
	const mainHeader = document.getElementById("main-header");
	const body = document.body;
	const hamburgerIcon = mobileMenuButton?.querySelector(".hamburger-icon");

	function toggleMobileMenu() {
		const isClosed = mobileMenu?.classList.contains("translate-x-full");

		if (isClosed) {
			// Primeiro apenas remove a classe hidden para o elemento se tornar visível
			if (mobileMenu?.classList.contains("hidden")) {
				mobileMenu.classList.remove("hidden");
			}

			body.classList.add("overflow-hidden");
			hamburgerIcon?.classList.add("active");
			mainHeader?.classList.remove("bg-accent/60");
			mainHeader?.classList.add("bg-accent");

			// Força um reflow para que o navegador processe a remoção da classe hidden
			// antes de iniciar a animação de translação
			mobileMenu?.getBoundingClientRect();

			// Pequeno delay para garantir que a animação de slide seja visível
			setTimeout(() => {
				mobileMenu?.classList.remove("opacity-0", "translate-x-full");
			}, 10);
		} else {
			mobileMenu?.classList.add("opacity-0", "translate-x-full");
			body.classList.remove("overflow-hidden");
			hamburgerIcon?.classList.remove("active");
			mainHeader?.classList.add("bg-accent/60");
			mainHeader?.classList.remove("bg-accent");

			// Mantém o delay para a animação de fechamento antes de esconder completamente
			setTimeout(() => {
				mobileMenu?.classList.add("hidden");
			}, 300);
		}
	}

	if (mobileMenuButton && mobileMenu) {
		// Certifique-se de que o menu comece com a classe hidden para evitar problemas visuais
		// durante a inicialização, mas mantenha as classes de translação para animação
		if (!mobileMenu.classList.contains("hidden")) {
			mobileMenu.classList.add("hidden");
		}

		mobileMenuButton.addEventListener("click", toggleMobileMenu);
	}

	// Fechar o menu ao clicar em um link
	const mobileMenuLinks = mobileMenu?.querySelectorAll("a");
	mobileMenuLinks?.forEach((link) => {
		link.addEventListener("click", toggleMobileMenu);
	});
}

// Inicializa a exibição de integrações
export function initializeIntegrationsDisplay() {
	const integrationButtons = document.querySelectorAll("[data-integration]");
	const allCards = document.querySelectorAll('[id^="integration-item-"]');
	const emptyCard = document.getElementById("empty-card");

	function hideAllCards() {
		allCards.forEach((card) => {
			(card as HTMLElement).style.opacity = "0";
			(card as HTMLElement).style.transform = "translateY(1rem)";
			setTimeout(() => {
				card.classList.add("opacity-0", "translate-y-4");
			}, 300);
		});
		if (emptyCard) {
			emptyCard.classList.add("hidden");
		}
	}

	function showCard(targetCard: Element) {
		targetCard.classList.remove("opacity-0", "translate-y-4");
		setTimeout(() => {
			(targetCard as HTMLElement).style.opacity = "1";
			(targetCard as HTMLElement).style.transform = "translateY(0)";
		}, 50);
		if (emptyCard) {
			emptyCard.classList.remove("hidden");
		}
	}

	integrationButtons.forEach((button) => {
		button.addEventListener("click", () => {
			const integrationName = button.getAttribute("data-integration");
			const targetCard = document.querySelector(
				`#integration-item-${integrationName}`,
			);
			if (targetCard) {
				hideAllCards();
				showCard(targetCard);
			}
		});
	});

	showCard(allCards[0]);
}

// Inicializa o menu "Mais"
export function initializeMoreMenu() {
	const moreButton = document.getElementById("more-button");
	const moreMenu = document.getElementById("more-menu");
	const chevron = moreButton?.querySelector(".lucide-chevron-down");

	function toggleMenu(expanded: boolean) {
		moreButton?.setAttribute("aria-expanded", (!expanded).toString());
		if (expanded) {
			moreMenu!.style.maxHeight = "0";
			moreMenu?.classList.replace("opacity-100", "opacity-0");
			moreMenu?.classList.replace("visible", "invisible");
			chevron?.classList.remove("rotate-180");
		} else {
			const menuHeight = moreMenu!.scrollHeight;
			const maxHeight = Math.min(menuHeight, window.innerHeight * 0.8);
			moreMenu!.style.maxHeight = `${maxHeight}px`;
			moreMenu?.classList.replace("opacity-0", "opacity-100");
			moreMenu?.classList.replace("invisible", "visible");
			chevron?.classList.add("rotate-180");

			const buttonRect = moreButton!.getBoundingClientRect();
			const menuRect = moreMenu!.getBoundingClientRect();
			const topPosition = Math.max(
				Math.min(
					buttonRect.bottom + 16,
					window.innerHeight - menuRect.height - 16,
				),
				20,
			);
			moreMenu!.style.top = `${topPosition}px`;
		}
	}

	moreButton?.addEventListener("click", () => {
		const expanded = moreButton.getAttribute("aria-expanded") === "true";
		toggleMenu(expanded);
	});

	document.addEventListener("click", (event) => {
		if (
			!moreButton?.contains(event.target as Node) &&
			!moreMenu?.contains(event.target as Node)
		) {
			toggleMenu(true);
		}
	});
}

// Inicializa a sidebar do FAQ
export function initializeFaqSidebar() {
	const sidebar = document.getElementById("faq-sidebar");
	const toggleButton = document.getElementById("sidebar-toggle");
	const toggleIcon = document.getElementById("toggle-icon");
	const body = document.body;

	function toggleSidebar() {
		const isOpen = !sidebar?.classList.contains("-translate-x-full");
		const header = document.getElementById("faq-header");

		sidebar?.classList.toggle("-translate-x-full");
		toggleIcon?.classList.toggle("rotate-180");
		body.classList.toggle("overflow-hidden");

		if (isOpen) {
			toggleButton!.style.transform = "translateX(0)";
			header?.classList.replace("bg-accent", "bg-accent/60");
		} else {
			toggleButton!.style.transform = "translateX(16rem)";
			header?.classList.replace("bg-accent/60", "bg-accent");
		}
	}

	toggleButton?.addEventListener("click", toggleSidebar);

	document.addEventListener("click", (event) => {
		if (
			!sidebar?.contains(event.target as Node) &&
			!toggleButton?.contains(event.target as Node) &&
			!sidebar?.classList.contains("-translate-x-full")
		) {
			toggleSidebar();
		}
	});

	sidebar?.querySelectorAll("a").forEach((link) => {
		link.addEventListener("click", () => {
			if (
				window.innerWidth < 1024 &&
				!sidebar.classList.contains("-translate-x-full")
			) {
				toggleSidebar();
			}
		});
	});
}

// Inicializa o alternador de tema
export function initializeThemeSwitcher() {
	const themeSwitcher = document.getElementById("theme-switcher");
	const htmlElement = document.documentElement;

	function setTheme(isDark: boolean) {
		if (isDark) {
			htmlElement.classList.add("dark");
			localStorage.setItem(keys.IS_DARK_MODE, "true");
		} else {
			htmlElement.classList.remove("dark");
			localStorage.setItem(keys.IS_DARK_MODE, "false");
		}
	}

	function toggleDarkMode() {
		const isDarkMode = htmlElement.classList.contains("dark");
		setTheme(!isDarkMode);
	}

	if (themeSwitcher) {
		// Inicializa o tema com base no localStorage ou na preferência do sistema
		const storedTheme = localStorage.getItem(keys.IS_DARK_MODE);
		const prefersDark = window.matchMedia(
			"(prefers-color-scheme: dark)",
		).matches;
		const initialIsDark =
			storedTheme === "true" || (storedTheme === null && prefersDark);
		setTheme(initialIsDark);

		themeSwitcher.addEventListener("click", toggleDarkMode);
	}
}

// Inicializa a barra de pesquisa da página de novidades
export function initializeNewsSearch() {
	const searchInput = document.getElementById(
		"search-news",
	) as HTMLInputElement;
	const searchResultsElement = document.getElementById("search-results");

	if (!searchInput || !searchResultsElement) return;

	// Obter todos os dados de pesquisa da API de constantes
	import("@/constants/landingPage").then(({ newsPageData }) => {
		const { sections } = newsPageData;

		// Obter recursos da tabela de comparação
		const resources = sections.toolsUpdate.comparisonTable.categories.flatMap(
			(category) => category.resources,
		);

		// Importar e configurar Fuse.js para busca fuzzy
		import("fuse.js").then((module) => {
			const Fuse = module.default;

			// Criar instância do Fuse com os recursos
			const fuse = new Fuse(resources, {
				keys: ["resource", "keywords"],
				threshold: 0.4, // Ajuste conforme necessidade
				ignoreLocation: true,
			});

			// Função para mostrar/esconder resultados
			function toggleResults(show: boolean) {
				if (show) {
					searchResultsElement?.classList.remove("hidden");
				} else {
					searchResultsElement?.classList.add("hidden");
				}
			}

			// Mostrar resultados ao focar na caixa de pesquisa
			searchInput.addEventListener("focus", () => toggleResults(true));

			// Esconder resultados ao clicar fora
			document.addEventListener("click", (e) => {
				if (e.target !== searchInput && e.target !== searchResultsElement) {
					toggleResults(false);
				}
			});

			// Função para normalizar string (similar ao useSearchInArray)
			function normalizeString(str: string): string {
				return str
					.toLowerCase()
					.normalize("NFD")
					.replace(/\p{Diacritic}/gu, "") // Remove acentos de forma mais segura
					.trim();
			}

			// Processar pesquisa ao digitar
			searchInput.addEventListener("input", () => {
				const query = searchInput.value.trim();

				// Se não houver consulta, limpar resultados
				if (!query) {
					if (searchResultsElement) searchResultsElement.innerHTML = "";
					return;
				}

				// Buscar com Fuse.js
				const fuseResults = fuse.search(normalizeString(query), { limit: 8 });
				const filteredResults = fuseResults.map((result) => result.item);

				// Atualizar resultados na interface
				renderResults(filteredResults);
			});

			// Renderizar resultados na interface
			function renderResults(results: typeof resources) {
				// Verificar se searchResultsElement ainda existe
				if (!searchResultsElement) return;

				// Limpar resultados anteriores
				searchResultsElement.innerHTML = "";

				// Se não houver resultados, mostrar mensagem
				if (results.length === 0) {
					searchResultsElement.innerHTML = `
					<div class="p-3 text-sm text-muted-foreground/70">
						Nenhum resultado encontrado.
					</div>
					`;
					return;
				}

				// Criar um item para cada resultado
				results.forEach((result) => {
					const resultItem = document.createElement("a");
					resultItem.href = result.link || "#";
					resultItem.className =
						"block p-3 text-sm text-foreground hover:bg-muted";
					resultItem.textContent = result.resource;

					// Limpar input e esconder resultados ao clicar
					resultItem.addEventListener("click", () => {
						searchInput.value = "";
						toggleResults(false);
					});

					searchResultsElement.appendChild(resultItem);
				});
			}
		});
	});
}

// Inicializa efeito de blur pulsante nas bolinhas flutuantes
export function initializeRadialPulseBackground() {
	const radialPulseElements = document.querySelectorAll(".radial-pulse");

	if (!radialPulseElements || radialPulseElements.length === 0) {
		return;
	}

	// Função para animar a pulsação
	function pulseAnimation(element: HTMLElement) {
		// Obter o elemento filho com o gradiente
		const gradientDiv = element.querySelector("div");
		if (!gradientDiv) return;

		// Alternar entre valores de opacidade
		let growing = true;
		let opacity = parseFloat(window.getComputedStyle(element).opacity);
		const minOpacity = opacity * 0.5; // 50% da opacidade original
		const maxOpacity = opacity * 2; // 200% da opacidade original (limitado a 1)

		function animate() {
			if (growing) {
				opacity += 0.0005;
				if (opacity >= maxOpacity) growing = false;
			} else {
				opacity -= 0.0005;
				if (opacity <= minOpacity) growing = true;
			}

			element.style.opacity = Math.min(1, opacity).toString();
			requestAnimationFrame(() => animate());
		}

		animate();
	}

	// Iniciar animação para cada elemento
	radialPulseElements.forEach((element) => {
		pulseAnimation(element as HTMLElement);
	});
}

// Inicializa efeito de brilho rotacionando no botão CTA
export function initializeGlowingBorderEffect() {
	const glowingButton = document.getElementById("glowing-cta-button");
	const glowEffect = document.getElementById("glow-effect");

	if (!glowingButton || !glowEffect) {
		return;
	}

	// Função para animar o brilho
	let rotation = 0;

	function animateGlow() {
		rotation = (rotation + 0.6) % 360;
		if (glowEffect) {
			// Aplicar apenas a transformação de rotação, mantendo as outras transformações do CSS
			const transform = `rotate(${rotation}deg) translateX(-0.75rem) translateY(1rem)`;
			glowEffect.style.transform = transform;
		}
		requestAnimationFrame(animateGlow);
	}

	// Iniciar animação
	animateGlow();

	// Adicionar efeito hover com classes Tailwind para melhor transição
	glowingButton.addEventListener("mouseenter", () => {
		if (glowEffect) {
			glowEffect.classList.remove("opacity-80");
			glowEffect.classList.add("opacity-100");
		}
	});

	glowingButton.addEventListener("mouseleave", () => {
		if (glowEffect) {
			glowEffect.classList.remove("opacity-100");
			glowEffect.classList.add("opacity-80");
		}
	});
}

// Inicializa os cards de atualização de estoque
export function initializeStockUpdates() {
	const container = document.getElementById("stock-updates-container");
	if (!container) return;

	const cards = Array.from(container.querySelectorAll("[data-stock-card]"));
	if (cards.length === 0) return;

	const currentIndex = 0;
	const minCards = 4;
	const maxCards = 5;
	let currentMaxCards = minCards;

	// Função para atualizar a visibilidade dos cards
	function updateCardsVisibility() {
		cards.forEach((card, index) => {
			if (index < currentMaxCards) {
				card.classList.remove("opacity-0", "translate-y-4");
				card.classList.add("opacity-100", "translate-y-0");
			} else {
				card.classList.remove("opacity-100", "translate-y-0");
				card.classList.add("opacity-0", "translate-y-4");
			}
		});

		// Alterna entre 4 e 5 cards
		currentMaxCards = currentMaxCards === minCards ? maxCards : minCards;
	}

	// Atualiza os cards a cada 3 segundos
	setInterval(() => {
		updateCardsVisibility();
	}, 3000);
}

// Inicializa os dropdowns da página de notícias
export function initializeNewsDropdowns() {
	const dropdownButtons = document.querySelectorAll("[data-news-dropdown]");

	dropdownButtons.forEach((button) => {
		// Obter o menu dropdown relacionado
		const dropdown = button.nextElementSibling;

		// Adicionar evento de clique
		button.addEventListener("click", (e) => {
			e.stopPropagation();

			// Fechar todos os outros dropdowns primeiro
			dropdownButtons.forEach((otherButton) => {
				if (otherButton !== button) {
					const otherDropdown = otherButton.nextElementSibling;
					if (otherDropdown) {
						otherDropdown.classList.add("hidden");
					}
				}
			});

			// Alternar a visibilidade do dropdown atual
			if (dropdown) {
				dropdown.classList.toggle("hidden");
			}
		});
	});

	// Fechar dropdowns ao clicar fora
	document.addEventListener("click", () => {
		dropdownButtons.forEach((button) => {
			const dropdown = button.nextElementSibling;
			if (dropdown && !dropdown.classList.contains("hidden")) {
				dropdown.classList.add("hidden");
			}
		});
	});

	// Evitar fechar ao clicar dentro do dropdown
	document
		.querySelectorAll("[data-news-dropdown-content]")
		.forEach((content) => {
			content.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		});
}
